import { SITE } from "@/common/seo.config";

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Remove nested @context so nodes are safe inside a root @graph. */
export function normalizeSchemaNode(node) {
  if (!node || typeof node !== "object") return node;
  const { ["@context"]: _ctx, ...rest } = node;
  return rest;
}

export function buildBreadcrumbList(items = []) {
  const list = items.filter((item) => item?.name && item?.path);
  if (!list.length) return null;

  return {
    "@type": "BreadcrumbList",
    itemListElement: list.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.path.startsWith("http")
        ? item.path
        : `${SITE.siteUrl}${item.path.startsWith("/") ? item.path : `/${item.path}`}`,
    })),
  };
}

export function buildFaqPage(faqs = [], { id } = {}) {
  const entities = (Array.isArray(faqs) ? faqs : [])
    .filter((item) => item?.question && item?.answer)
    .map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: stripHtml(item.answer),
      },
    }));

  if (!entities.length) return null;

  return {
    "@type": "FAQPage",
    ...(id ? { "@id": id } : {}),
    mainEntity: entities,
  };
}

export function buildWebPage({
  path,
  name,
  description,
  type = "WebPage",
  extras = {},
}) {
  const url = path.startsWith("http") ? path : `${SITE.siteUrl}${path}`;
  return {
    "@type": type,
    "@id": `${url}#webpage`,
    url,
    name,
    description,
    isPartOf: { "@id": `${SITE.siteUrl}/#website` },
    about: { "@id": `${SITE.siteUrl}/#organization` },
    publisher: { "@id": `${SITE.siteUrl}/#organization` },
    inLanguage: "en-US",
    ...extras,
  };
}

export function buildServiceSchema({
  name,
  description,
  path,
  serviceType,
  areaServed = "Worldwide",
}) {
  const url = `${SITE.siteUrl}${path}`;
  return {
    "@type": "Service",
    "@id": `${url}#service`,
    name,
    description,
    url,
    serviceType: serviceType || name,
    provider: { "@id": `${SITE.siteUrl}/#organization` },
    areaServed,
    brand: {
      "@type": "Brand",
      name: SITE.siteName,
    },
  };
}

export function buildHowToSchema({ name, description, path, steps = [] }) {
  const url = `${SITE.siteUrl}${path}`;
  return {
    "@type": "HowTo",
    "@id": `${url}#howto`,
    name,
    description,
    url,
    step: steps.map((step, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: step.name || step.title,
      text: step.text || step.description,
    })),
  };
}

export function buildItemListSchema({ name, path, items = [] }) {
  const url = `${SITE.siteUrl}${path}`;
  const list = items.filter((item) => item?.name && item?.path);
  if (!list.length) return null;

  return {
    "@type": "ItemList",
    "@id": `${url}#itemlist`,
    name,
    itemListElement: list.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      url: item.path.startsWith("http")
        ? item.path
        : `${SITE.siteUrl}${item.path}`,
    })),
  };
}

export function buildCollectionPage({
  path,
  name,
  description,
  type = "CollectionPage",
}) {
  return buildWebPage({ path, name, description, type });
}

export function pageSchemas(nodes = []) {
  return nodes.filter(Boolean).map(normalizeSchemaNode);
}
